// Global navbar search — queries the main entity tables in parallel and
// returns a categorised result set the dropdown can render directly.
//
// Categories: people, tickets, channels, messages, events, documents, notes, kb.
// Each category caps at 5 hits.
//
// migrated 2026-04-27 to SDK route workspaceProject.search (rpc).
//
// The server wraps `workspace_global_search(q, surfaces, limit)` — a single
// SQL function the wrapper exposes via /search. The function is expected
// to return rows with at least { surface, id, title, subtitle, href }
// fields. While migrations land, surfaces lacking server-side support are
// quietly elided. People are still resolved client-side from `root.people`.
//
// TODO[server]: wire the workspace_global_search() SQL function so all
// surfaces fan out server-side; once it ships, surface-specific queries
// here are no longer needed.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
// The `integration_kinds` CATALOGUE read ("what could you connect?") — no org
// context, no configured instances, exactly the shape the zero-result
// suggestion pass wants. SDK-only, like every other read in this file.
const shellListIntegrationKinds = (...args) => _wsShared.functions.listIntegrationKinds(...args)
// Canonical EditorJS body→plain-text helpers — the SAME transform
// documents/functions/index.js's documentsFetch/reloadDocsAfterSwitch use, so
// a search hit's `_bodyText` matches what the /documents grid already
// derived (no re-implementation to drift out of sync).
import { _docContent, _joinBodyText } from '@symbo-ls/workspace-documents/functions/parseBody.js'
// WS-SWITCH-INCOMPLETE-1 — the canonical late-reply guard (functions/
// sameWorkspaceWriter.js). See the terminal write below for why this file's
// own `root.navSearchQuery !== query` guard is not enough.
import { sameWorkspaceWriter } from '../sameWorkspaceWriter.js'
// The launcher's OWN group builder — already feature-flag-gated, admin-gated
// and per-viewer filtered, so the app pass below can never surface an app the
// apps launcher hides for this viewer. Imported directly for the same reason
// components/SearchAppsMenu.js imports it directly: it is a plain builder, not
// an `el.call` function, and its own deferred `getViewNav()` keeps it safe to
// pull in this early (see searchApps.js's own note).
import { searchAppGroupsFor } from '../searchApps.js'
import { appSearchHits, appSuggestionHits } from './searchAppResults.js'
// The ⌘K palette's COMMAND rows — the shell keyboard registry's rows matched
// against the query (DESIGN-WL-SHELL-KEYBOARD-MOTION-1). A plain builder, not
// an `el.call` function, imported directly for the same reason as the app
// builders above.
import { commandSearchHits } from './searchCommandResults.js'
// The rows the installed apps opt into the search (CMDK-FINDS-APP-RECORDS-1).
import {
  APP_RECORDS_ORDER,
  APP_RECORDS_SURFACE,
  SEARCH_ACTIONS_CATEGORY,
  SEARCH_CHIPS_CATEGORY,
  SEARCH_FILTERS_CATEGORY,
  activeChipRows,
  appRecordSearchHits,
  facetFilterRows,
  isAppRecordsCategory,
  searchActionRows,
  searchChipKey,
  searchHandoffHref,
  searchMatchTotal,
  serializeSearchChips
} from './searchAppRecordResults.js'
// The ask hand-off (CMDK-08) goes through the same door an installed app's
// "Ask" does — the context rides the turn structured (moduleAskBridge.js).
import { buildModuleAskBridge } from '../moduleAskBridge.js'

const _LIMIT = 5
const _DEBOUNCE_MS = 200
// Client-side pass cache TTL — shared by the documents pass (_docHits) and the
// files pass (_fileHits) below.
const _PASS_CACHE_MS = 30000
// The suggestion catalogues (App Store projects, integration_kinds) are
// STATIC platform data, not tenant rows that change under the user — so they
// hold far longer than the 30s tenant-row passes above. They are also read on
// the zero-result path, which fires per KEYSTROKE while somebody types a word
// nothing matches ("c" → "cr" → "crm"): a short TTL there is three network
// round-trips for one answer.
const _CATALOG_CACHE_MS = 300000
// Below this length a zero-result query is almost always a half-typed word,
// so the suggestion pass skips the two catalogue reads and returns only its
// (network-free) browse rows. The menu is still never empty.
const _SUGGEST_MIN_CHARS = 2

// Category metadata for display ordering and labels.
//
// `kind` selects the ROW RENDERER: 'record' (the default) is
// NavSearchResultItem, 'apps' is NavSearchAppItem — the AppListItem-derived
// launcher row, so an app in the results reads exactly like the same app in
// the apps launcher. NavSearchResultGroup branches on it (components/
// NavSearchDropdown.js); `_groupResults` stamps it onto every group.
const CATEGORY_META = {
  // Apps the workspace ALREADY has (built-in tiles + installed apps), FIRST
  // in the menu (2026-09-10 per Nika "search should also search and suggest
  // apps"): a one-word query — "mail", "calendar", "files" — nearly always
  // means "take me there", and a navigation answer buried under five ticket
  // titles is an answer nobody sees.
  apps: { label: 'workspace.search.category.apps', icon: 'blocks', order: -1, kind: 'apps' },
  // COMMANDS — the shell keyboard registry's rows (HOME_AMBIENT_SKIN_SPEC §5:
  // "The command palette (ws-w2) lists the same registry rows with keycaps").
  // Right after the apps: like an app, a command answers "do this now", and
  // a query only ever matches one by its own label or group, so it never
  // crowds out a record. NavSearchCommandItem renders the rows; a pick runs
  // the command (runSearchCommand below), it never navigates to a href.
  commands: {
    label: 'workspace.search.category.commands',
    icon: 'keyboard',
    order: -0.5,
    kind: 'commands'
  },
  // ⌘K FILTER CHIPS (CMDK-05) and what to do with the chipped result
  // (CMDK-08) — searchAppRecordResults.js builds the rows, NavSearchActionItem
  // renders them, a pick runs the row's `_searchAction` (runSearchAction
  // below). The active chips head the list; the values to filter by and the
  // actions close it, after every record group.
  [SEARCH_CHIPS_CATEGORY]: {
    label: 'workspace.search.category.chips',
    icon: 'filter',
    order: -2,
    kind: 'actions'
  },
  [SEARCH_FILTERS_CATEGORY]: {
    label: 'workspace.search.category.filters',
    icon: 'filter',
    order: 20,
    kind: 'actions'
  },
  [SEARCH_ACTIONS_CATEGORY]: {
    label: 'workspace.search.category.actions',
    icon: 'arrowRight',
    order: 21,
    kind: 'actions'
  },
  people: { label: 'workspace.search.category.people', icon: 'people', order: 0 },
  tickets: { label: 'workspace.search.category.tickets', icon: 'check', order: 1 },
  channels: { label: 'workspace.search.category.channels', icon: 'chat', order: 2 },
  messages: { label: 'workspace.search.category.messages', icon: 'chat', order: 3 },
  // The server's OWN `mail` surface (WorkspaceSearchService SURFACES, row href
  // `/mail?thread=<id>`), so a mirrored thread reads as Mail instead of the raw
  // surface name the fallback below would print. Label/icon mirror AppNavbar's
  // `mail` entry (icon 'email', route /mail); the order seats it between chat
  // and calendar, which is where a reader looks for something somebody sent
  // them (MAIL-SEARCH-UI-CATEGORY-ROW-1).
  mail: { label: 'workspace.search.category.mail', icon: 'email', order: 4 },
  events: { label: 'workspace.search.category.events', icon: 'date', order: 5 },
  // 'documents' remains as a category key only for SERVER-side surface hits
  // (ALL_SURFACES) — client doc hits all classify as notes/kb now that the
  // surface renamed to Notes.
  documents: { label: 'workspace.search.category.notes', icon: 'document', order: 6 },
  notes: { label: 'workspace.search.category.notes', icon: 'document', order: 7 },
  kb: { label: 'workspace.search.category.kb', icon: 'bookmark', order: 8 },
  // Label/icon mirror AppNavbar's `files` entry (icon 'folder', route /files).
  files: { label: 'workspace.search.category.files', icon: 'folder', order: 9 },
  // ── Zero-result suggestion groups ─────────────────────────────────────────
  // Present ONLY when nothing else matched (see the suggestion pass in
  // globalSearch below), hence the far-away order numbers: they can never
  // interleave with real hits, and if the suggestion pass ever ran alongside
  // results they would still sort to the bottom.
  appstore: {
    label: 'workspace.search.category.appStore',
    icon: 'blocks',
    order: 90,
    kind: 'apps'
  },
  integrations: {
    label: 'workspace.search.category.integrations',
    icon: 'api',
    order: 91,
    kind: 'apps'
  },
  discover: { label: 'workspace.search.category.discover', icon: 'search', order: 92, kind: 'apps' }
}

// Map a server-side row {surface, id, title, ...} to the client-side
// flat result shape used by the dropdown. Surfaces without an explicit
// mapping fall through to a generic shape — let the server label them.
const _SURFACE_HREF = {
  tickets: (r) => `/tasks/my?ticket=${r.number || r.id}`,
  notes: (r) => `/notes?id=${r.id}`,
  kb: (r) => `/notes/knowledge-base?id=${r.id}`,
  events: (r) => `/calendar?event=${r.id}`,
  channels: (r) => `/chat?channel=${r.id}`,
  messages: (r) => `/chat?channel=${r.channel_id}&message=${r.id}`,
  documents: (r) => `/notes?id=${r.id}`,
  // `?node=<id>` is the /files desktop's universal-link deep open — it reopens
  // that exact node once its canvas has loaded (files/pages/files.js).
  files: (r) => `/files?node=${r.id}`
}

// The doc family: one doc, many ids (its docId slug, its Mongo _id). Its
// docId is the id the notes surface opens (`/notes?id=<docId>`) — a server
// row that carries it links there, not to the server's own href.
const _DOC_SURFACES = new Set(['documents', 'notes', 'kb'])

const _shapeRow = (row) => {
  const surface = row.surface || row._category
  const docId = _DOC_SURFACES.has(surface) && row.docId ? row.docId : null
  const id = docId || row.id || row._id
  const title = row.title || row._title || `#${row.number || id}`
  const subtitle = row.subtitle || row._subtitle || row.author || ''
  const hrefBuilder = _SURFACE_HREF[surface]
  return {
    _category: surface,
    _id: id,
    _title: title,
    _subtitle: subtitle,
    _href:
      docId && hrefBuilder
        ? hrefBuilder({ ...row, id: docId })
        : row.href || row._href || (hrefBuilder ? hrefBuilder(row) : '#')
  }
}

// ── Documents/notes/KB client-side pass ─────────────────────────────────────
// The `workspaceProject.search` RPC below never shipped server-side (the SQL
// function doesn't exist — it always throws, caught below) and the ONLY
// client-side fallback pass was people, so `docs` rows never appeared in the
// dropdown at all (commit cfdc18b6 removed the other client-side surface
// queries). Mirrors documents/functions/index.js's reloadDocsAfterSwitch
// exactly: entity name is `docs` (NOT `documents` — that's the SDK route the
// /documents page's OWN fetch uses), same row transform, so a search hit and
// the /documents grid describe the identical row shape.
//
// Module-level, workspace-keyed, 30s cache: typing "docu…" character by
// character must not re-issue docs.select() on every keystroke, and a
// workspace switch must not leak the previous tenant's rows into a fresh
// query.
let _docsSearchCache = { key: null, rows: null, at: 0 }

const _workspaceCacheKey = (root) =>
  String(root?.workspace?.id ?? root?.workspace?._id ?? root?.org?.id ?? root?.org?._id ?? '')

// Row `type` values from the `docs` collection are the Mongo discriminator
// names (document/kb_article/note/user_document — see server DocService) —
// clean, unambiguous, and already what documentsFetch's transform carries
// through as-is. Splits the flat search hit into the SAME three categories
// the rest of this file already declares (CATEGORY_META, _SURFACE_HREF)
// rather than dumping every doc-family row under "documents".
const _DOC_TYPE_CATEGORY = { note: 'notes', kb_article: 'kb' }

const _docHits = async (root, ql) => {
  let rows
  try {
    const key = _workspaceCacheKey(root)
    const now = Date.now()
    if (root?._documentsLoaded && Array.isArray(root.documents) && root.documents.length) {
      rows = root.documents
    } else if (
      _docsSearchCache.rows &&
      _docsSearchCache.key === key &&
      now - _docsSearchCache.at < _PASS_CACHE_MS
    ) {
      rows = _docsSearchCache.rows
    } else {
      // `list`, NOT `select`. The `docs` entity route exposes
      // list/get/create/update/remove/tree/folders — `select` is only an
      // ADAPTER-level alias (shared/functions/sdkAdapter.js maps it onto
      // `list` for declarative `fetch:` configs) and does not exist on a
      // direct sdk.execute call, so this threw on EVERY keystroke:
      //   "Entity 'docs' does not support op 'select'"
      // The catch below swallowed it and returned [], which is why Documents
      // / Notes / Knowledge Base never appeared in the dropdown unless
      // /documents had already been visited this session (the root.documents
      // fast path above). Verified live 2026-08-05.
      const res = await shellGetSDK().execute('docs', 'list', {})
      const raw = Array.isArray(res) ? res : res?.data || []
      rows = raw.map((r) => ({
        id: r.docId || r._id || r.id,
        // every id the doc answers to — the merge drops a server row that
        // names the same doc by another of them
        _ids: [r.docId, r._id, r.id].filter(Boolean).map(String),
        title: r.title || 'workspace.search.untitled',
        folder: r.folderPath || r.folder || 'company',
        type: r.type || 'doc',
        _bodyText: _joinBodyText(_docContent(r.body || r.content))
      }))
      _docsSearchCache = { key, rows, at: now }
    }
  } catch (err) {
    console.warn('[globalSearch] documents fetch failed', err?.message || err)
    return []
  }

  const matched = (rows || []).filter(
    (d) =>
      (d.title || '').toLowerCase().includes(ql) || (d._bodyText || '').toLowerCase().includes(ql)
  )
  // Each of the three doc-family categories caps at _LIMIT independently
  // (matching every other category in this file — see the header comment)
  // so e.g. five matching notes can't crowd a matching document out of the
  // dropdown entirely.
  const byCategory = { documents: [], notes: [], kb: [] }
  for (const d of matched) {
    const category = _DOC_TYPE_CATEGORY[d.type] || 'notes'
    const bucket = byCategory[category]
    if (bucket.length >= _LIMIT) continue
    const hrefBuilder = _SURFACE_HREF[category]
    bucket.push({
      _category: category,
      _id: d.id,
      _title: d.title || 'workspace.search.untitledDocument',
      _subtitle: d.folder || '',
      _href: hrefBuilder ? hrefBuilder(d) : `/notes?id=${d.id}`,
      _altIds: (d._ids || [d.id, d._id, d.docId]).filter(Boolean).map(String)
    })
  }
  return [...byCategory.documents, ...byCategory.notes, ...byCategory.kb]
}

// ── Files client-side pass ──────────────────────────────────────────────────
// Files had no representation in search at all before 2026-08-05 (per Nika
// "why documents and files are not in search dropdown"): no CATEGORY_META row,
// no href builder, no fetch pass — and no `files` surface server-side either
// (server ALL_SURFACES covers tickets/channels/messages/events/documents/
// notes/kb only), so a client pass is the only way to reach them today.
//
// Source is `workspaceProject.fileCanvas` — the /files desktop's own node
// table (files/functions/fetchConfigs.js fileCanvasFetch), already on root as
// `fileNodes` once /files has been visited. Deliberately NOT sdk.files.*:
// FileService has no entity route and its only list-shaped read is
// listMyUploads() (per-user uploads, not the workspace's shared desktop).
//
// Same module-level, workspace-keyed, 30s-cached shape as _docHits above, and
// for the same two reasons: typing character by character must not re-issue
// the list on every keystroke, and a workspace switch must not leak the
// previous tenant's rows into a fresh query.
let _filesSearchCache = { key: null, rows: null, at: 0 }

const _fileHits = async (root, ql) => {
  let rows
  try {
    const key = _workspaceCacheKey(root)
    const now = Date.now()
    if (root?._fileNodesLoaded && Array.isArray(root.fileNodes) && root.fileNodes.length) {
      rows = root.fileNodes
    } else if (
      _filesSearchCache.rows &&
      _filesSearchCache.key === key &&
      now - _filesSearchCache.at < _PASS_CACHE_MS
    ) {
      rows = _filesSearchCache.rows
    } else {
      // `list` for the same reason the docs pass above uses it — `select` is
      // an adapter-only alias and throws on a direct sdk.execute call.
      const res = await shellGetSDK().execute('workspaceProject.fileCanvas', 'list', {})
      const raw = Array.isArray(res) ? res : res?.data || []
      // Mirrors fileCanvasFetch's transform for the fields search needs, so a
      // search hit and the /files desktop describe the identical row.
      rows = raw.map((r) => ({
        id: r.id,
        name: r.name || 'workspace.search.untitled',
        kind: r.kind,
        mime: r.mime_type || r.mime || null
      }))
      _filesSearchCache = { key, rows, at: now }
    }
  } catch (err) {
    console.warn('[globalSearch] files fetch failed', err?.message || err)
    return []
  }

  return (rows || [])
    .filter((f) => (f.name || '').toLowerCase().includes(ql))
    .slice(0, _LIMIT)
    .map((f) => ({
      _category: 'files',
      _id: f.id,
      _title: f.name || 'workspace.search.untitled',
      _subtitle: f.kind === 'folder' ? 'workspace.search.folder' : f.mime || '',
      _href: `/files?node=${f.id}`
    }))
}

// ── App records pass (CMDK-FINDS-APP-RECORDS-1) ─────────────────────────────
// The rows the installed apps opt into the workspace search — their manifest's
// `collections[].search`: fleet nodes, incidents, networks. The server answers
// (SDK `search.workspace`, surface 'app-records'): only opted-in collections,
// under the caller's records grant and field rules, matched on the declared
// fields — so a guest without a key finds none of the collections it cannot
// read. No cache: the rows are live data and the call is one indexed query.
// The app-records pass: the rows, plus — for an app that declares them — the
// chip values with their counts and the app pages that take the matched rows
// (CMDK-05 / CMDK-08). `q` already carries the active chips as `field:value`
// tokens (serializeSearchChips), so the server scopes everything to them.
const _appRecordSearch = async (el, root, q) => {
  const none = { hits: [], facets: [], handoffs: [], totals: [], total: 0, totalCapped: false }
  let data
  try {
    const res = await shellGetSDK().execute('search', 'workspace', {
      query: q,
      surfaces: [APP_RECORDS_SURFACE],
      limit: _LIMIT
    })
    data = res && res.data && !Array.isArray(res.data) ? res.data : res
  } catch (err) {
    console.warn('[globalSearch] app records pass threw', err?.message || err)
    return none
  }
  // The server names each app by its manifest's first tile or tab (its
  // search opt-in index); a member reads the App Store's name for it — Nika
  // 2026-10-23: "app names = what a person sees in the workspace App Store".
  // So every `app` the reply carries is named here, once, through THE
  // store-copy resolver with the app's record key (the last segment of its
  // `owner/key` id): the group label, a value's list hint and the "Open in
  // <app>" hand-off all read these records, and the rows are transient — the
  // next query builds them again, in the language of that moment. An app the
  // store ships no name for keeps the server's words.
  const _named = (row) => {
    const app = row && row.app
    if (!app || typeof app !== 'object' || typeof app.name !== 'string') return row
    const id = typeof app.id === 'string' ? app.id : ''
    const key = id.includes('/') ? id.split('/').pop() : ''
    if (!key) return row
    let name = ''
    try {
      name = el.call('storeItemText', { key, name: app.name }, 'name')
    } catch (_) {
      name = ''
    }
    return typeof name === 'string' && name && name !== app.name
      ? { ...row, app: { ...app, name } }
      : row
  }
  const _list = (v) => (Array.isArray(v) ? v.map(_named) : [])
  const results = _list(data?.results)
  const handoffs = _list(data?.handoffs)
  const totals = Array.isArray(data?.totals) ? data.totals : []
  // How many rows the search matched — the server's totals, the same rows the
  // chip counts come from; never the page shown (searchMatchTotal).
  const matched = searchMatchTotal({ totals, handoffs, results, limit: _LIMIT })
  return {
    hits: appRecordSearchHits(results, {
      hrefFor: (ref) => el.call('entityRefHref', ref, root),
      iconFor: (ref) => el.call('entityRefIcon', ref),
      limit: _LIMIT
    }),
    facets: _list(data?.facets),
    handoffs,
    totals,
    total: matched.count,
    totalCapped: matched.capped
  }
}

// ── Zero-result suggestion catalogues ───────────────────────────────────────
// Read ONLY on the zero-result path, so an ordinary search never pays for
// them. Both are static platform data (see _CATALOG_CACHE_MS).
//
// The App Store catalog is NOT re-fetched here: `loadWorkspaceAppsCatalog`
// (functions/workspaceApps.js) is the ONE reader of the `workspaceModule:
// true` projects list and it already caches onto the root
// (`_wsAppsCatalog` / `_wsAppsCatalogLoaded` / `_wsAppsCatalogLoading`), so a
// suggestion and the /add-app rail can never disagree about what the store
// holds. A load already in flight returns early without populating — that
// keystroke simply falls back to the browse rows and the next one has it.
const _storeCatalog = async (el, root) => {
  if (Array.isArray(root?._wsAppsCatalog) && root._wsAppsCatalog.length) return root._wsAppsCatalog
  if (root?._wsAppsCatalogLoaded) return root._wsAppsCatalog || []
  try {
    await el.call('loadWorkspaceAppsCatalog')
  } catch (err) {
    console.warn('[globalSearch] app-store catalog unavailable', err?.message || err)
    return []
  }
  return root?._wsAppsCatalog || []
}

// Module-level, workspace-keyed cache — same shape and the same two reasons
// as _docsSearchCache / _filesSearchCache above (no re-issue per keystroke, no
// tenant leak across a switch), on the longer catalogue TTL.
let _integrationKindsCache = { key: null, rows: null, at: 0 }

const _integrationKindHits = async (root) => {
  const key = _workspaceCacheKey(root)
  const now = Date.now()
  if (
    _integrationKindsCache.rows &&
    _integrationKindsCache.key === key &&
    now - _integrationKindsCache.at < _CATALOG_CACHE_MS
  ) {
    return _integrationKindsCache.rows
  }
  try {
    const rows = await shellListIntegrationKinds()
    const list = Array.isArray(rows) ? rows : []
    // Only a NON-EMPTY read is cached. An empty one is indistinguishable from
    // "the SDK had no session yet" — pinning that for five minutes would hide
    // the whole catalogue for the rest of a boot that simply asked too early.
    // Block body, not a one-liner: `__tests__/moduleLetCensus.test.js` reads a
    // single-line `if (…) <symbol> = …` followed by a `return` as a SKIP GUARD
    // on a module `let` (the "already did this" shape it exists to catch), and
    // this is a write, not a guard.
    if (list.length) {
      _integrationKindsCache = { key, rows: list, at: now }
    }
    return list
  } catch (err) {
    console.warn('[globalSearch] integration kinds unavailable', err?.message || err)
    return []
  }
}

// Group flat results by category, respecting display order. Each item gets
// a `_flatIndex` — a running index across the WHOLE grouped set (group order
// first, then item order within a group) — so the keyboard-navigation path
// (moveNavSearchSelection / activateNavSearchSelection below) and each
// NavSearchResultItem row (NavSearchDropdown.js) can agree on "which row is
// #3" without either side re-deriving the flatten order independently.
// Exported (as `groupSearchResults`, below) purely so the CONTRACT the
// dropdown reads — group order and the `kind` that picks the row renderer —
// is assertable without mounting DOMQL or stubbing three network passes.
const _groupResults = (results) => {
  const grouped = {}
  for (const r of results) {
    if (!grouped[r._category]) grouped[r._category] = []
    grouped[r._category].push(r)
  }
  let flatIndex = 0
  // An app-records group (one per app) carries its OWN label — the app's name,
  // tenant content — and icon on its rows, and sits right after People.
  const orderOf = (cat) =>
    CATEGORY_META[cat]?.order ?? (isAppRecordsCategory(cat) ? APP_RECORDS_ORDER : 99)
  return Object.entries(grouped)
    .sort(([a], [b]) => orderOf(a) - orderOf(b))
    .map(([cat, items]) => ({
      category: cat,
      label: CATEGORY_META[cat]?.label || items[0]?._groupLabel || cat,
      icon: CATEGORY_META[cat]?.icon || items[0]?._groupIcon || 'search',
      // Which ROW component renders this group — see CATEGORY_META above. A
      // server surface with no entry here falls back to 'record', which is
      // what every category rendered as before app rows existed.
      kind: CATEGORY_META[cat]?.kind || 'record',
      items: items.map((it) => ({ ...it, _flatIndex: flatIndex++ }))
    }))
}

// Total item count across every group — moveNavSearchSelection's wrap bound.
const _flatCount = (grouped) =>
  Array.isArray(grouped) ? grouped.reduce((n, g) => n + (g.items?.length || 0), 0) : 0

// A "Pin to navbar" context menu on a result row (NavSearchResultItem.
// onContextmenu, NavSearchDropdown.js — opened with `fromNavSearch: true`)
// can outlive the keystroke that started this call: globalSearch's own
// debounce (await below) + network round-trip gives a right-click a real
// window to open the menu WHILE a call is still in flight — the call then
// resolves after and would otherwise SWAP OUT the rows the menu's
// `searchResult` payload was captured from, even though HeaderSearch's /
// IntranetAppHeader's blur guard correctly kept `_navSearchOpen` true (a
// true `_navSearchOpen` is necessary but not sufficient — the ROWS must
// stay on screen too). Only the POST-AWAIT write below needs this: the
// empty-query short-circuit and the very first sync update above never
// cross an await, so they can't land after the menu opens — `onInput`
// (globalSearch's own trigger) can't even fire while the menu has focus
// moved off the input. Every close path (Escape, outside click, the Pin
// action) nulls `contextDropdown`, so live search resumes the instant it
// closes — nothing here to separately track or clean up.
const _navSearchMenuOpen = (root) =>
  !!(root?.contextDropdown?.open && root.contextDropdown.fromNavSearch)

export const globalSearch = async function (query) {
  const root = this.state.root
  const q = (query || '').trim()
  // ⛔ CAPTURED HERE, BEFORE THE DEBOUNCE — not at the write site below.
  // WS-SWITCH-INCOMPLETE-1: `navSearchResults` / `_navSearchGrouped` are
  // workspace-scoped (state-app.js), so the switch empties them. That closes
  // the persistent case only. This call outlives its tenant by design — a
  // 200ms debounce plus three network passes — and its ONLY in-flight guard
  // was `root.navSearchQuery !== query`. The query text is the user's own
  // input and is deliberately UNSCOPED, so it survives the switch UNCHANGED:
  // after A -> B the query guard still MATCHES and A's tickets, notes and
  // files land in B's dropdown, where Enter calls openInSlot with A's href.
  // A query guard is not a tenant guard.
  const _write = sameWorkspaceWriter(root)
  // ⌘K FILTER CHIPS (CMDK-05): with a chip active the palette is a filtered
  // list of the apps' rows — it stays open with no text, and only the
  // app-records pass runs (the chips ride its query). `chipsSig` pins this
  // call to the chips it was issued for: a chip added or removed while it was
  // in flight makes its answer stale, exactly like a changed query.
  const chips = Array.isArray(root._navSearchChips) ? root._navSearchChips : []
  const chipsSig = serializeSearchChips(chips)
  const filtering = !!chipsSig
  // Stale-results fix (SEARCH-1, 2026-08-14): every new query resets the
  // keyboard selection immediately — a row highlighted for the PREVIOUS
  // query must never look "selected" against results that no longer belong
  // to it. `_navSearchLoading` (already written here) is the single source
  // of truth the dropdown reads to DIM the still-on-screen previous-query
  // rows and show a loading indicator until this exact call's results land
  // (see HeaderSearch.js's Dropdown for the render side).
  root.update({
    navSearchQuery: query || '',
    _navSearchLoading: !!q || filtering,
    _navSearchOpen: !!q || filtering,
    _navSearchActiveIndex: -1
  })
  if (!q && !filtering) {
    root.update({
      navSearchResults: [],
      _navSearchGrouped: [],
      _navSearchLoading: false,
      _navSearchOpen: false,
      _navSearchActiveIndex: -1
    })
    return []
  }
  await new Promise((r) => setTimeout(r, _DEBOUNCE_MS))
  if (root.navSearchQuery !== query) return root.navSearchResults || []
  const _stale = () =>
    root.navSearchQuery !== query ||
    serializeSearchChips(Array.isArray(root._navSearchChips) ? root._navSearchChips : []) !==
      chipsSig
  if (filtering) {
    const rec = await _appRecordSearch(this, root, q ? `${chipsSig} ${q}` : chipsSig)
    if (_stale()) return root.navSearchResults || []
    const rows = [
      ...activeChipRows(chips),
      ...rec.hits,
      ...facetFilterRows(rec.facets, { term: q, chips }),
      ...searchActionRows({
        chips,
        handoffs: rec.handoffs,
        total: rec.total,
        totalCapped: rec.totalCapped,
        totals: rec.totals
      })
    ]
    if (!_navSearchMenuOpen(root)) {
      const landed = _write({ navSearchResults: rows, _navSearchGrouped: _groupResults(rows) })
      root.update(
        landed
          ? { _navSearchLoading: false, _navSearchOpen: true }
          : { _navSearchLoading: false, _navSearchOpen: false, _navSearchActiveIndex: -1 }
      )
    }
    return rec.hits.map((h) => ({
      id: h._id,
      title: h._title,
      category: h._category,
      subtitle: h._subtitle || null,
      href: h._href || null
    }))
  }
  const ql = q.toLowerCase()
  // APP hits (2026-09-10 per Nika "search should also search and suggest
  // apps"). Purely local — the launcher's already-gated group list plus a
  // string match — so it costs no round-trip and needs no cache, and it runs
  // BEFORE the network passes so the cheap navigation answer is ready the
  // moment anything else lands. `this.call('polyglot', …)` resolves a shipped
  // tile's key so a Georgian session matches Georgian words; the row keeps
  // the KEY, so the rendered label still re-translates on a language switch.
  const _t = (key) => {
    try {
      return this.call('polyglot', key || '')
    } catch (_) {
      return key || ''
    }
  }
  // Guarded like every other pass in this file: a launcher tile's own
  // `visible()` is module-supplied code, and a throwing module must degrade
  // search to "no app rows", never to "no search".
  // An installed app answers to the name the member reads — the App Store's
  // (Nika 2026-10-23: "app names = what a person sees in the workspace App
  // Store") — through THE store-copy resolver, the same call the row's title
  // makes (AppListItem). The shared resolver, not the shell's
  // installedAppName: the comm app runs this pass before the shell registry
  // folds in (comm/functions.js), and the shared library is there at boot.
  const _name = (tile) => {
    try {
      return this.call('storeItemText', { key: tile.appKey, name: tile.title || '' }, 'name')
    } catch (_) {
      return ''
    }
  }
  let appHits = []
  try {
    // …and the person's own apps (Your apps — BLD-28: a saved draft is found
    // from any page), after the workspace's: an app already in the workspace
    // answers once, as its own tile.
    appHits = appSearchHits(searchAppGroupsFor(root), ql, {
      translate: _t,
      name: _name,
      limit: _LIMIT,
      userApps: root && root._userApps
    })
  } catch (err) {
    console.warn('[globalSearch] apps pass threw', err?.message || err)
  }
  // COMMAND hits (DESIGN-WL-SHELL-KEYBOARD-MOTION-1): the shell keyboard
  // registry's rows (`listShortcuts` — the ? sheet's own source), matched on
  // their translated label and group. Local like the app pass, and guarded the
  // same way: a registry that throws costs the command rows, never the search.
  // `fallbackLang: 'en'` lets a Georgian session answer "keyboard" too — the
  // app pass reaches English through its keys and routes the same way.
  let commandHits = []
  try {
    const _tIn = (key, lang) => {
      try {
        return lang ? this.call('polyglot', key || '', lang) : this.call('polyglot', key || '')
      } catch (_) {
        return key || ''
      }
    }
    commandHits = commandSearchHits(this.call('listShortcuts') || [], ql, {
      translate: _tIn,
      fallbackLang: 'en',
      limit: _LIMIT
    })
  } catch (err) {
    console.warn('[globalSearch] commands pass threw', err?.message || err)
  }
  // Fire alongside the server RPC below — both are network-bound (the docs
  // pass has its own 30s cache, so this only actually hits the network on a
  // cache-cold workspace), no reason to serialize them.
  const docsPromise = _docHits(root, ql).catch((err) => {
    console.warn('[globalSearch] documents pass threw', err?.message || err)
    return []
  })
  // Files ride alongside the docs pass and the server RPC for the same reason:
  // all three are network-bound (this one has its own 30s cache, so it only
  // actually hits the network on a cache-cold workspace).
  const filesPromise = _fileHits(root, ql).catch((err) => {
    console.warn('[globalSearch] files pass threw', err?.message || err)
    return []
  })
  // The installed apps' opted-in rows (CMDK-FINDS-APP-RECORDS-1) — network-
  // bound like the two passes above, so it rides alongside them.
  const recordsPromise = _appRecordSearch(this, root, q).catch((err) => {
    console.warn('[globalSearch] app records pass threw', err?.message || err)
    return { hits: [], facets: [], handoffs: [], totals: [], total: 0, totalCapped: false }
  })
  let serverResults = []
  try {
    const result = await shellGetSDK().execute('workspaceProject.search', 'rpc', {
      q,
      options: { limit: _LIMIT }
    })
    if (root.navSearchQuery !== query) return root.navSearchResults || []
    const rows = Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : []
    serverResults = rows.map(_shapeRow)
  } catch (err) {
    // Swallow until the server-side search RPC ships. People-only fallback
    // still renders so the dropdown never goes empty.
    console.warn(
      '[globalSearch] server search unavailable, returning people-only',
      err?.message || err
    )
  }

  if (root.navSearchQuery !== query) return root.navSearchResults || []
  const docHits = await docsPromise
  const fileHits = await filesPromise
  const records = await recordsPromise
  const recordHits = records.hits
  // A chip value the typed text names ("Pinecrest" → "Customer · Pinecrest
  // Custody · 3"): picking it turns the search into a filtered list (CMDK-05).
  const filterHits = facetFilterRows(records.facets, { term: q })
  if (root.navSearchQuery !== query) return root.navSearchResults || []
  const peopleHits = (root.people || [])
    .filter(
      (p) => (p.name || '').toLowerCase().includes(ql) || (p.email || '').toLowerCase().includes(ql)
    )
    .slice(0, _LIMIT)
    .map((p) => ({
      _category: 'people',
      _id: p.email,
      _title: p.name || p.email,
      _subtitle: p.email,
      _href: `/people?u=${encodeURIComponent(p.email)}`
    }))
  // ONE DOC, ONE ROW (lane TOOLS item F; dev 2026-10-27: "Lead Generation
  // Strategy" listed twice — the docs pass by its docId, the server search by
  // its _id — and the server row's link opened an empty note). The docs pass
  // row wins (its `/notes?id=<docId>` opens the doc); a server row of the doc
  // family naming the same doc — by any of its ids, or by its title — goes.
  const sameTitle = (t) =>
    String(t || '')
      .trim()
      .toLowerCase()
  const docIds = new Set()
  const docTitles = new Set()
  for (const h of docHits) {
    for (const id of h._altIds || [h._id]) docIds.add(String(id))
    docTitles.add(sameTitle(h._title))
  }
  const serverRows = serverResults.filter(
    (r) =>
      !(
        _DOC_SURFACES.has(r._category) &&
        (docIds.has(String(r._id)) || docTitles.has(sameTitle(r._title)))
      )
  )
  const docRows = docHits.map(({ _altIds, ...row }) => row)
  const all = [
    ...appHits,
    ...commandHits,
    ...peopleHits,
    ...recordHits,
    ...docRows,
    ...fileHits,
    ...serverRows,
    ...filterHits
  ]
  // NOTHING matched — offer what this workspace could ADD instead of an empty
  // menu (2026-09-10 per Nika "if nothing found - it should suggest from
  // appstore, marketplace or integrations"). Only here, only on a real zero,
  // and the two catalogue reads are skipped for a half-typed query
  // (_SUGGEST_MIN_CHARS) — the browse rows alone already make the menu
  // answerable, so a one-character miss costs no network at all.
  if (!all.length) {
    const wide = q.length >= _SUGGEST_MIN_CHARS
    const catalog = wide ? await _storeCatalog(this, root) : []
    const kinds = wide ? await _integrationKindHits(root) : []
    // The catalogue reads are awaits, so re-check the query the same way
    // every other pass above does — a suggestion set for an abandoned query
    // must never land over the answer to the current one.
    if (root.navSearchQuery !== query) return root.navSearchResults || []
    // `el: this` — the store rule runs through the element (searchAppResults.js)
    all.push(
      ...appSuggestionHits(ql, { catalog, integrationKinds: kinds, limit: _LIMIT, el: this })
    )
  }
  // Guarded write — see _navSearchMenuOpen above: a "Pin to navbar" menu
  // opened on one of the CURRENTLY-VISIBLE rows must not have those rows
  // swapped out from under it by this call finally landing. `_navSearchOpen`
  // is left untouched (still true, both because it already is and because
  // the blur guard is what's keeping it true) — only the rows/loading flag
  // are held back; live search resumes with fresh data the instant the menu
  // closes (nothing to reconcile — the next keystroke's call writes normally).
  if (!_navSearchMenuOpen(root)) {
    // ⛔ THE PATCH SPLITS, and it must. The ROWS are tenant data, so they are
    // dropped when this reply outlived its workspace. The two transport flags
    // are this call's own terminal state, and they are deliberately UNSCOPED —
    // the switch's wipe never reaches them. Dropping them together with the
    // rows would strand the dropdown dimmed-with-a-spinner over an empty list
    // for the rest of the session: a leak fix that becomes a stuck surface.
    // A request cancelled by a switch is still a request that has STOPPED.
    const landed = _write({ navSearchResults: all, _navSearchGrouped: _groupResults(all) })
    root.update(
      landed
        ? { _navSearchLoading: false, _navSearchOpen: true }
        : { _navSearchLoading: false, _navSearchOpen: false, _navSearchActiveIndex: -1 }
    )
  }
  // Return the hits so the open_search client tool can hand them to the model
  // (it was returning undefined — the AI saw the search "ran" but got zero rows,
  // so it could never chain a hit into select_page_item / move_ticket). Compact,
  // model-friendly shape. The human dropdown reads root.navSearchResults above.
  // Keyboard COMMAND rows stay out: they are this person's key bindings, not
  // places or records the model can open (no href, a bundle key for a title).
  return all
    .filter((h) => h._category !== 'commands' && !h._searchAction)
    .map((h) => ({
      id: h._id,
      title: h._title,
      category: h._category,
      subtitle: h._subtitle || null,
      href: h._href || null
    }))
}

export const closeNavSearch = function () {
  const root = this.state.root
  root.update({
    navSearchQuery: '',
    // A closed search drops its chips: a palette reopened later starts
    // unfiltered, never silently scoped by an earlier session's chips.
    _navSearchChips: [],
    navSearchResults: [],
    _navSearchGrouped: [],
    _navSearchLoading: false,
    _navSearchOpen: false,
    _navSearchActiveIndex: -1
  })
  // The search is over, so is its dropdown: the launcher hosts the results
  // (9A), and a closed search must not leave it standing. Its own dismissal
  // stamp keeps the focus hand-back from opening it again. Reached through
  // `this.call` and availability-checked (a test root or a standalone bundle
  // has no launcher) — never a module helper: every export here is
  // serialized on its own (FA204).
  try {
    if (typeof this?.context?.functions?.closeSearchLauncher === 'function') {
      this.call('closeSearchLauncher')
    }
  } catch (_) {
    /* a launcher already gone is the goal */
  }
  // Search app selections consume this intent in the same event. Escape
  // and outside dismissal leave it unused, so remove the empty split pane.
  if (root._slotSplitIntent) {
    queueMicrotask(() => {
      if (root._slotSplitIntent && !root._navSearchOpen) root.update({ _slotSplitIntent: false })
    })
  }
}

export const navigateSearchResult = function (href) {
  const root = this.state.root
  root.update({
    _headerSearchOpen: false,
    navSearchQuery: '',
    _navSearchChips: [],
    navSearchResults: [],
    _navSearchGrouped: [],
    _navSearchLoading: false,
    _navSearchOpen: false,
    _navSearchActiveIndex: -1
  })
  // A picked record row ends the search — the launcher that listed it closes
  // with it (9A: the rows live in the launcher). Same self-contained call as
  // closeNavSearch's.
  try {
    if (typeof this?.context?.functions?.closeSearchLauncher === 'function') {
      this.call('closeSearchLauncher')
    }
  } catch (_) {
    /* a launcher already gone is the goal */
  }
  // Open-in-slot-vs-navigate (incl. the tickets external-entry-point
  // carve-out — a /tickets subview floating over /studio reads wrong) is
  // centralized in openInSlot (shared/functions/openInSlot.js) so search and
  // every other opener (notifications, toasts, …) can never drift.
  this.call('openInSlot', href, this)
}

// Open an APP row (a `_appRoute`-carrying result — the apps group and every
// zero-result suggestion group). Deliberately NOT navigateSearchResult:
// openInSlot only knows slot-vs-navigate, while `selectAppFromSwitcher` is
// the launcher's own opener and is the ONE place that knows a canvas surface
// must land full-page, that /admin + /data carry their own sidebar, and that
// everything else belongs in the PageSlot. Picking Calendar in search must
// land exactly where picking Calendar in the apps launcher lands — routing an
// app through openInSlot is how the two drift.
export const navigateSearchApp = function (route) {
  if (!route) return
  closeNavSearch.call(this)
  // Result activation ends the search gesture. Keeping the morph open
  // leaves HeaderTabs (including Open apps) transparent and non-interactive.
  this.state.root.update({ _headerSearchOpen: false })
  this.call('selectAppFromSwitcher', route)
}

// Run a COMMAND row (a `_shortcutId`-carrying result — the ⌘K palette's rows,
// the shell keyboard registry's own; DESIGN-WL-SHELL-KEYBOARD-MOTION-1). The
// search closes FIRST — query, rows, selection and the header morph — so the
// command lands on the surface under it (the composer takes focus, the ?
// sheet opens over the page, a Go to row navigates from where you are), then
// the registry runs the row (`runShortcut`: the row's `when` still applies;
// the keystroke switches do not — a pick is a command, not a key). Returns
// whether the command ran. Self-contained: every read goes through `this`.
export const runSearchCommand = function (id) {
  if (!id) return false
  const root = this.state && this.state.root
  this.call('closeNavSearch')
  if (root && typeof root.update === 'function') root.update({ _headerSearchOpen: false })
  return this.call('runShortcut', id) === true
}

// Run a CHIP or ACTION row (an `_searchAction`-carrying result — CMDK-05 /
// CMDK-08; searchAppRecordResults.js builds them):
//   - chip-add / chip-remove / chips-clear: the chips change and the search
//     runs again over them — the typed text is spent by a pick (it chose the
//     chip), kept by a remove; the palette stays open while a chip is left;
//   - handoff: the app's own page with the matched rows
//     (`/validators/intel?show=a,b`), opened the way a record row opens;
//   - ask: the workspace assistant takes the question — the typed text, or
//     "tell me about these results" — with the chips and the rows riding the
//     turn as an inspected region (moduleAskBridge.js, the app "Ask" door).
// Returns whether the action ran.
export const runSearchAction = function (action) {
  const root = this.state && this.state.root
  if (!root || !action || typeof action !== 'object') return false
  const chips = Array.isArray(root._navSearchChips) ? root._navSearchChips : []
  const query = root.navSearchQuery || ''
  // The field takes focus FIRST, then the search runs. A field that had lost
  // focus re-runs the search from its focus handler with the text still in it
  // (HeaderSearch openSearchSurface reads the DOM value, which follows
  // navSearchQuery one tick late); the search issued after it supersedes that
  // run. The other order let the stale text win: live on dev 34259e777
  // (2026-10-07), a chip picked after the field lost focus kept "Pinecrest"
  // as a term, and the term emptied the "Filter by" group.
  if (action.type === 'chip-add' && action.chip) {
    const key = searchChipKey(action.chip)
    if (!key || chips.some((c) => searchChipKey(c) === key)) return false
    const { field, value, label, fieldLabel } = action.chip
    root.update({ _navSearchChips: [...chips, { field, value, label, fieldLabel }] })
    this.call('focusHeaderSearch')
    this.call('globalSearch', '')
    return true
  }
  if (action.type === 'chip-remove') {
    root.update({ _navSearchChips: chips.filter((c) => searchChipKey(c) !== action.key) })
    this.call('focusHeaderSearch')
    this.call('globalSearch', query)
    return true
  }
  if (action.type === 'chips-clear') {
    root.update({ _navSearchChips: [] })
    this.call('focusHeaderSearch')
    this.call('globalSearch', query)
    return true
  }
  if (action.type === 'handoff') {
    const href = searchHandoffHref(action)
    if (!href) return false
    this.call('navigateSearchResult', href)
    return true
  }
  if (action.type === 'ask') {
    const filters = chips
      .map((c) => `${c.fieldLabel || c.field}: ${c.label || c.value}`)
      .join(' · ')
    const rows = (Array.isArray(root.navSearchResults) ? root.navSearchResults : []).filter(
      (r) => r && typeof r._id === 'string' && r._id.startsWith('ref:record:')
    )
    const prompt =
      query.trim() ||
      this.call('polyglot', 'workspace.search.action.askPrompt').replace('{filters}', filters)
    const context = {
      title: this.call('polyglot', 'workspace.search.action.askContext').replace(
        '{filters}',
        filters
      ),
      text: rows
        .map((r) => r._title)
        .filter(Boolean)
        .join(', '),
      records: rows.map((r) => r._id.slice('ref:record:'.length).replace('/', ':'))
    }
    this.call('closeNavSearch')
    root.update({ _headerSearchOpen: false })
    buildModuleAskBridge(root).call(this, prompt, { context })
    return true
  }
  return false
}

// A search row's words (NavSearchActionItem): tenant content as given, or a
// bundle KEY resolved through polyglot with its `{param}` values filled in.
export const navSearchRowText = function (text, key, params) {
  if (typeof text === 'string' && text) return text
  if (typeof key !== 'string' || !key) return ''
  let out = this.call('polyglot', key)
  if (typeof out !== 'string') return ''
  for (const [k, v] of Object.entries(params && typeof params === 'object' ? params : {})) {
    out = out.split(`{${k}}`).join(String(v))
  }
  return out
}

// Arrow-key navigation through the grouped results (SEARCH-1, 2026-08-14).
// `delta` is +1 (Down) or -1 (Up); wraps at both ends like every other
// house listbox (HeaderTabs, MenuPanel). No-ops on an empty result set —
// there's nothing to select.
export const moveNavSearchSelection = function (delta) {
  const root = this.state.root
  const count = _flatCount(root?._navSearchGrouped)
  if (!count) return
  const hasSelection =
    typeof root._navSearchActiveIndex === 'number' && root._navSearchActiveIndex >= 0
  // No selection yet: Down starts at the top (index 0), Up starts at the
  // bottom (index count-1) — the same "Up from nothing lands on the last
  // row" convention every house listbox uses. Modeled as the position we'd
  // be moving FROM: one before the top for Down, the top itself for Up (so
  // subtracting 1 wraps to the bottom).
  const cur = hasSelection ? root._navSearchActiveIndex : delta > 0 ? -1 : 0
  const next = (((cur + delta) % count) + count) % count
  root.update({ _navSearchActiveIndex: next })
}

// Enter — open the keyboard-highlighted result. Defaults to the FIRST result
// when nothing has been arrow-key-selected yet (the same "Enter just works"
// convention Slack/Linear/Spotlight use), so a query typed and immediately
// confirmed with Enter behaves the same as clicking the top hit.
export const activateNavSearchSelection = function () {
  const root = this.state.root
  const grouped = root?._navSearchGrouped
  const count = _flatCount(grouped)
  if (!count) return
  const idx =
    typeof root._navSearchActiveIndex === 'number' && root._navSearchActiveIndex >= 0
      ? root._navSearchActiveIndex
      : 0
  let seen = 0
  for (const g of grouped) {
    const items = g.items || []
    if (idx < seen + items.length) {
      const target = items[idx - seen]
      // An APP row opens through the launcher's opener, a record row through
      // openInSlot — the same split the two ROW components make on click, so
      // Enter and a click can never send the same row to two different
      // containers.
      // A COMMAND row runs through the keyboard registry (runSearchCommand) —
      // the same call its click makes.
      if (target?._appRoute) this.call('navigateSearchApp', target._appRoute)
      else if (target?._shortcutId) this.call('runSearchCommand', target._shortcutId)
      else if (target?._searchAction) this.call('runSearchAction', target._searchAction)
      else if (target?._href) this.call('navigateSearchResult', target._href)
      return
    }
    seen += items.length
  }
}

export { CATEGORY_META, _groupResults as groupSearchResults }
